import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tab,
  Tabs,
  Grid,
  Menu,
  Tooltip,
  Avatar,
  AvatarGroup,
  Switch,
  FormControlLabel,
  Collapse,
  Alert,
  Snackbar,
  Badge,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  MoreVert as MoreVertIcon,
  Repeat as RepeatIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SubdirectoryArrowRight as SubtaskIcon,
  NotificationsActive as ReminderIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  ViewList as ListViewIcon,
  ViewModule as BoardViewIcon,
  Schedule as ScheduleIcon,
  Assignment as TaskIcon,
  Comment as CommentIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import { format, isToday, isTomorrow, isPast, parseISO, differenceInDays } from 'date-fns';
import api, { handleApiError } from '../../utils/api';

interface Subtask {
  id: string;
  title: string;
  completed: boolean;
}

interface Reminder {
  type: 'notification' | 'email';
  date: string;
  time: string;
}

interface Comment {
  id: string;
  text: string;
  author: string;
  createdAt: Date;
}

interface Attachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string;
  dueTime: string;
  starred: boolean;
  projectId: string;
  tags: string[];
  subtasks: Subtask[];
  progress: number;
  estimatedTime: number; // in minutes
  actualTime: number; // in minutes
  isRecurring: boolean;
  recurrence?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    endDate?: string;
  };
  reminders: Reminder[];
  attachments: Attachment[];
  assignees: string[];
  comments: Comment[];
  status: 'not-started' | 'in-progress' | 'blocked' | 'completed';
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
  archivedAt?: Date;
}

interface Project {
  id: string;
  name: string;
  color: string;
  icon: string;
  taskCount: number;
}

type ViewMode = 'list' | 'board';
type SortOption = 'dueDate' | 'priority' | 'created' | 'alphabetical';

const priorityColors: Record<string, string> = {
  urgent: '#d32f2f',
  high: '#f44336',
  medium: '#ff9800',
  low: '#4caf50',
};

const statusColors: Record<string, string> = {
  'not-started': '#9e9e9e',
  'in-progress': '#2196f3',
  'blocked': '#ff5722',
  'completed': '#4caf50',
};

const TasksPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  const mapTask = (t: any): Task => ({
    ...t,
    id: t._id || t.id,
    dueDate: t.dueDate ? format(new Date(t.dueDate), 'yyyy-MM-dd') : '',
    createdAt: new Date(t.createdAt),
    updatedAt: new Date(t.updatedAt),
    completedAt: t.completedAt ? new Date(t.completedAt) : undefined,
  });

  const mapProject = (p: any): Project => ({
    id: p._id || p.id,
    name: p.name,
    color: p.color,
    icon: p.icon || '📁',
    taskCount: p.taskCount || 0,
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await api.get('/tasks/projects');
      const data = res.data?.data || res.data || [];
      setProjects(Array.isArray(data) ? data.map(mapProject) : []);
    } catch { /* ignore */ }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await api.get('/tasks');
      const data = res.data?.data || res.data || [];
      setTasks(Array.isArray(data) ? data.map(mapTask) : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchTasks();
  }, [fetchProjects, fetchTasks]);

  // Listen for global refresh event from header
  useEffect(() => {
    const handleRefresh = () => { fetchProjects(); fetchTasks(); };
    window.addEventListener('app-refresh', handleRefresh);
    return () => window.removeEventListener('app-refresh', handleRefresh);
  }, [fetchProjects, fetchTasks]);

  const [allTags] = useState<string[]>(['urgent', 'review', 'meeting', 'report', 'weekly', 'planning', 'groceries', 'important', 'follow-up']);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [tabValue, setTabValue] = useState(0);
  const [sortOption, setSortOption] = useState<SortOption>('dueDate');
  const [searchQuery, setSearchQuery] = useState('');
  const [, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [taskMenuAnchor, setTaskMenuAnchor] = useState<{ anchor: HTMLElement; task: Task } | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState('#1976d2');

  const [newTask, setNewTask] = useState<Partial<Task>>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: format(new Date(), 'yyyy-MM-dd'),
    dueTime: '',
    starred: false,
    projectId: 'inbox',
    tags: [],
    subtasks: [],
    progress: 0,
    estimatedTime: 60,
    actualTime: 0,
    isRecurring: false,
    recurrence: undefined,
    reminders: [],
    attachments: [],
    assignees: [],
    comments: [],
    status: 'not-started',
  });

  const getDueDateLabel = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return { label: 'Today', color: 'primary' };
    if (isTomorrow(date)) return { label: 'Tomorrow', color: 'default' };
    if (isPast(date)) return { label: `Overdue (${differenceInDays(new Date(), date)}d)`, color: 'error' };
    const diff = differenceInDays(date, new Date());
    if (diff <= 7) return { label: `In ${diff} days`, color: 'default' };
    return { label: format(date, 'MMM d'), color: 'default' };
  };

  const filteredTasks = useMemo(() => {
    let result = tasks.filter(task => {
      // Project filter
      if (selectedProjectId !== 'all' && task.projectId !== selectedProjectId) return false;
      
      // Tab filter
      if (tabValue === 0 && task.completed) return false; // Active
      if (tabValue === 1 && !task.completed) return false; // Completed
      if (tabValue === 2 && !task.starred) return false; // Starred
      if (tabValue === 3 && !isToday(parseISO(task.dueDate))) return false; // Today
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return task.title.toLowerCase().includes(query) ||
               task.description.toLowerCase().includes(query) ||
               task.tags.some(t => t.toLowerCase().includes(query));
      }
      
      return true;
    });

    // Sort
    result.sort((a, b) => {
      switch (sortOption) {
        case 'dueDate':
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case 'priority':
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'created':
          return b.createdAt.getTime() - a.createdAt.getTime();
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return result;
  }, [tasks, selectedProjectId, tabValue, searchQuery, sortOption]);

  const handleAddTask = () => {
    setEditingTask(null);
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: format(new Date(), 'yyyy-MM-dd'),
      dueTime: '',
      starred: false,
      projectId: selectedProjectId === 'all' ? 'inbox' : selectedProjectId,
      tags: [],
      subtasks: [],
      progress: 0,
      estimatedTime: 60,
      actualTime: 0,
      isRecurring: false,
      recurrence: undefined,
      reminders: [],
      attachments: [],
      assignees: [],
      comments: [],
      status: 'not-started',
    });
    setOpenDialog(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setNewTask({ ...task });
    setOpenDialog(true);
    setTaskMenuAnchor(null);
  };

  const handleSaveTask = async () => {
    if (!newTask.title?.trim()) {
      setSnackbar({ open: true, message: 'Task title is required', severity: 'error' });
      return;
    }

    let progress = 0;
    if (newTask.subtasks && newTask.subtasks.length > 0) {
      progress = Math.round((newTask.subtasks.filter(s => s.completed).length / newTask.subtasks.length) * 100);
    }

    const payload = {
      title: newTask.title!,
      description: newTask.description || '',
      completed: newTask.status === 'completed',
      priority: newTask.priority || 'medium',
      dueDate: newTask.dueDate || null,
      dueTime: newTask.dueTime || '',
      starred: newTask.starred || false,
      projectId: newTask.projectId || 'default',
      tags: newTask.tags || [],
      subtasks: newTask.subtasks || [],
      progress,
      estimatedTime: newTask.estimatedTime || 0,
      actualTime: newTask.actualTime || 0,
      isRecurring: newTask.isRecurring || false,
      recurrence: newTask.recurrence,
      reminders: newTask.reminders || [],
      assignees: newTask.assignees || [],
      status: newTask.status || 'not-started',
    };

    try {
      if (editingTask) {
        await api.put(`/tasks/${editingTask.id}`, payload);
        setSnackbar({ open: true, message: 'Task updated', severity: 'success' });
      } else {
        await api.post('/tasks', payload);
        setSnackbar({ open: true, message: 'Task created', severity: 'success' });
      }
      await fetchTasks();
      setOpenDialog(false);
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleToggleComplete = async (id: string) => {
    try {
      await api.put(`/tasks/${id}/complete`);
      await fetchTasks();
    } catch { /* ignore */ }
  };

  const handleToggleStar = async (id: string) => {
    try {
      await api.put(`/tasks/${id}/star`);
      await fetchTasks();
    } catch { /* ignore */ }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      await api.delete(`/tasks/${id}`);
      await fetchTasks();
      setTaskMenuAnchor(null);
      setSnackbar({ open: true, message: 'Task deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleDuplicateTask = async (task: Task) => {
    try {
      const { id, createdAt, updatedAt, completedAt, ...rest } = task;
      await api.post('/tasks', { ...rest, title: `${task.title} (copy)`, completed: false, status: 'not-started' });
      await fetchTasks();
      setTaskMenuAnchor(null);
      setSnackbar({ open: true, message: 'Task duplicated', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const handleToggleSubtask = async (taskId: string, subtaskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const subtasks = task.subtasks.map(s =>
      s.id === subtaskId ? { ...s, completed: !s.completed } : s
    );
    const progress = subtasks.length > 0 ? Math.round((subtasks.filter(s => s.completed).length / subtasks.length) * 100) : 0;
    try {
      await api.put(`/tasks/${taskId}`, { subtasks, progress });
      await fetchTasks();
    } catch { /* ignore */ }
  };

  const handleAddSubtask = () => {
    const newSubtask: Subtask = {
      id: `s${Date.now()}`,
      title: '',
      completed: false,
    };
    setNewTask({
      ...newTask,
      subtasks: [...(newTask.subtasks || []), newSubtask],
    });
  };

  const handleAddReminder = () => {
    setNewTask({
      ...newTask,
      reminders: [...(newTask.reminders || []), { type: 'notification', date: format(new Date(), 'yyyy-MM-dd'), time: '09:00' }],
    });
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      await api.post('/tasks/projects', { name: newProjectName, color: newProjectColor });
      await fetchProjects();
      setNewProjectName('');
      setProjectDialogOpen(false);
      setSnackbar({ open: true, message: 'Project created', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: handleApiError(err), severity: 'error' });
    }
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const totalTasks = tasks.length;
  const progress = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;
  const todayTasksCount = tasks.filter(t => !t.completed && isToday(parseISO(t.dueDate))).length;
  const overdueCount = tasks.filter(t => !t.completed && isPast(parseISO(t.dueDate)) && !isToday(parseISO(t.dueDate))).length;

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', bgcolor: '#f5f5f5' }}>
      {/* Left Sidebar - Projects */}
      <Paper sx={{ width: 240, m: 2, mr: 0, p: 2, display: 'flex', flexDirection: 'column' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTask}
          sx={{ mb: 2 }}
          fullWidth
        >
          New Task
        </Button>

        {/* Quick Stats */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Today</Typography>
            <Badge badgeContent={todayTasksCount} color="primary" max={99}>
              <CalendarIcon fontSize="small" color="action" />
            </Badge>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">Overdue</Typography>
            <Badge badgeContent={overdueCount} color="error" max={99}>
              <ScheduleIcon fontSize="small" color="action" />
            </Badge>
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Projects List */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>PROJECTS</Typography>
          <IconButton size="small" onClick={() => setProjectDialogOpen(true)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        <List dense disablePadding>
          <ListItemButton
            selected={selectedProjectId === 'all'}
            onClick={() => setSelectedProjectId('all')}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <TaskIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="All Tasks" />
            <Typography variant="caption" color="text.secondary">{totalTasks}</Typography>
          </ListItemButton>

          {projects.map(project => (
            <ListItemButton
              key={project.id}
              selected={selectedProjectId === project.id}
              onClick={() => setSelectedProjectId(project.id)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Typography>{project.icon}</Typography>
              </ListItemIcon>
              <ListItemText primary={project.name} />
              <Typography variant="caption" color="text.secondary">
                {tasks.filter(t => t.projectId === project.id && !t.completed).length}
              </Typography>
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Tags */}
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>TAGS</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {allTags.slice(0, 6).map(tag => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
              onClick={() => setSearchQuery(tag)}
            />
          ))}
        </Box>

        {/* Progress */}
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Overall Progress
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          <Typography variant="caption" color="text.secondary">
            {completedCount} of {totalTasks} completed
          </Typography>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Header */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              {selectedProjectId === 'all' ? 'All Tasks' : projects.find(p => p.id === selectedProjectId)?.name || 'Tasks'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="List view">
                <IconButton 
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                >
                  <ListViewIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Board view">
                <IconButton 
                  onClick={() => setViewMode('board')}
                  color={viewMode === 'board' ? 'primary' : 'default'}
                >
                  <BoardViewIcon />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Sort">
                <IconButton onClick={(e) => setSortMenuAnchor(e.currentTarget)}>
                  <SortIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Filter">
                <IconButton onClick={(e) => setFilterMenuAnchor(e.currentTarget)}>
                  <FilterIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
            sx={{ width: 300 }}
          />
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label={`Active (${tasks.filter(t => !t.completed).length})`} />
            <Tab label={`Completed (${completedCount})`} />
            <Tab label={`Starred (${tasks.filter(t => t.starred).length})`} />
            <Tab label={`Today (${todayTasksCount})`} />
          </Tabs>
        </Paper>

        {/* Task List */}
        <Paper sx={{ flex: 1, overflow: 'auto' }}>
          {filteredTasks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {tabValue === 0 ? 'No active tasks' : tabValue === 1 ? 'No completed tasks' : tabValue === 2 ? 'No starred tasks' : 'No tasks due today'}
              </Typography>
              <Button startIcon={<AddIcon />} onClick={handleAddTask} sx={{ mt: 2 }}>
                Add a task
              </Button>
            </Box>
          ) : viewMode === 'list' ? (
            <List disablePadding>
              {filteredTasks.map((task, index) => {
                const dueInfo = getDueDateLabel(task.dueDate);
                const isExpanded = expandedTaskId === task.id;
                const project = projects.find(p => p.id === task.projectId);

                return (
                  <React.Fragment key={task.id}>
                    {index > 0 && <Divider />}
                    <Box>
                      <ListItem
                        sx={{
                          opacity: task.completed ? 0.6 : 1,
                          '&:hover': { bgcolor: 'action.hover' },
                          alignItems: 'flex-start',
                        }}
                      >
                        <ListItemIcon sx={{ mt: 1 }}>
                          <Checkbox
                            checked={task.completed}
                            onChange={() => handleToggleComplete(task.id)}
                            icon={<UncheckedIcon />}
                            checkedIcon={<CheckCircleIcon color="success" />}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography 
                                sx={{ 
                                  textDecoration: task.completed ? 'line-through' : 'none', 
                                  fontWeight: 500,
                                  color: task.completed ? 'text.secondary' : 'inherit',
                                }}
                              >
                                {task.title}
                              </Typography>
                              <Chip
                                label={task.priority}
                                size="small"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.65rem',
                                  bgcolor: priorityColors[task.priority],
                                  color: 'white',
                                }}
                              />
                              <Chip
                                label={task.status.replace('-', ' ')}
                                size="small"
                                sx={{ 
                                  height: 20, 
                                  fontSize: '0.65rem',
                                  bgcolor: statusColors[task.status],
                                  color: 'white',
                                }}
                              />
                              {task.isRecurring && (
                                <Tooltip title={`Repeats ${task.recurrence?.frequency}`}>
                                  <RepeatIcon fontSize="small" color="action" />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {task.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {task.description.substring(0, 100)}{task.description.length > 100 ? '...' : ''}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {project && (
                                  <Chip 
                                    label={project.name} 
                                    size="small" 
                                    variant="outlined"
                                    icon={<Typography fontSize="small">{project.icon}</Typography>}
                                    sx={{ height: 22 }}
                                  />
                                )}
                                <Chip
                                  icon={<CalendarIcon />}
                                  label={dueInfo.label}
                                  size="small"
                                  color={dueInfo.color as any}
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                                {task.dueTime && (
                                  <Chip
                                    icon={<ScheduleIcon />}
                                    label={task.dueTime}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                                {task.estimatedTime > 0 && (
                                  <Chip
                                    icon={<ScheduleIcon />}
                                    label={formatDuration(task.estimatedTime)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                )}
                                {task.tags.map(tag => (
                                  <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                ))}
                              </Box>

                              {/* Subtasks progress */}
                              {task.subtasks.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  <SubtaskIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="text.secondary">
                                    {task.subtasks.filter(s => s.completed).length}/{task.subtasks.length} subtasks
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={task.progress}
                                    sx={{ flex: 1, maxWidth: 100, height: 4, borderRadius: 2 }}
                                  />
                                </Box>
                              )}

                              {/* Assignees */}
                              {task.assignees.length > 0 && (
                                <AvatarGroup max={4} sx={{ mt: 1, justifyContent: 'flex-start' }}>
                                  {task.assignees.map((email, idx) => (
                                    <Tooltip key={idx} title={email}>
                                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                        {email.charAt(0).toUpperCase()}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                </AvatarGroup>
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {task.attachments.length > 0 && (
                              <Badge badgeContent={task.attachments.length} color="default">
                                <AttachFileIcon fontSize="small" color="action" />
                              </Badge>
                            )}
                            {task.comments.length > 0 && (
                              <Badge badgeContent={task.comments.length} color="default" sx={{ mx: 1 }}>
                                <CommentIcon fontSize="small" color="action" />
                              </Badge>
                            )}
                            <IconButton onClick={() => handleToggleStar(task.id)} size="small">
                              {task.starred ? <StarIcon color="warning" /> : <StarBorderIcon />}
                            </IconButton>
                            <IconButton 
                              onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
                              size="small"
                            >
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <IconButton 
                              onClick={(e) => setTaskMenuAnchor({ anchor: e.currentTarget, task })}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>

                      {/* Expanded Subtasks */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ pl: 8, pr: 2, pb: 2 }}>
                          {task.subtasks.length > 0 && (
                            <List dense disablePadding>
                              {task.subtasks.map(subtask => (
                                <ListItem key={subtask.id} disablePadding>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Checkbox
                                      size="small"
                                      checked={subtask.completed}
                                      onChange={() => handleToggleSubtask(task.id, subtask.id)}
                                      icon={<UncheckedIcon fontSize="small" />}
                                      checkedIcon={<CheckCircleIcon fontSize="small" color="success" />}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={subtask.title}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      sx: { textDecoration: subtask.completed ? 'line-through' : 'none' }
                                    }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}

                          {task.attachments.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">Attachments:</Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                {task.attachments.map(att => (
                                  <Chip
                                    key={att.id}
                                    icon={<AttachFileIcon />}
                                    label={att.name}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(att.url)}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            // Board View
            <Box sx={{ display: 'flex', gap: 2, p: 2, minHeight: '100%' }}>
              {['not-started', 'in-progress', 'blocked', 'completed'].map(status => (
                <Paper key={status} variant="outlined" sx={{ flex: 1, minWidth: 250, p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, textTransform: 'capitalize' }}>
                    <Chip 
                      label={status.replace('-', ' ')} 
                      size="small" 
                      sx={{ bgcolor: statusColors[status], color: 'white' }}
                    />
                    <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                      ({filteredTasks.filter(t => t.status === status).length})
                    </Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredTasks.filter(t => t.status === status).map(task => (
                      <Paper 
                        key={task.id} 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          borderLeft: `4px solid ${priorityColors[task.priority]}`,
                        }}
                        onClick={() => handleEditTask(task)}
                      >
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                          {task.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon fontSize="small" color="action" sx={{ fontSize: 14 }} />
                          <Typography variant="caption" color="text.secondary">
                            {getDueDateLabel(task.dueDate).label}
                          </Typography>
                        </Box>
                        {task.subtasks.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            <LinearProgress variant="determinate" value={task.progress} sx={{ height: 3 }} />
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Task Context Menu */}
      <Menu
        anchorEl={taskMenuAnchor?.anchor}
        open={Boolean(taskMenuAnchor)}
        onClose={() => setTaskMenuAnchor(null)}
      >
        <MenuItem onClick={() => taskMenuAnchor && handleEditTask(taskMenuAnchor.task)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => taskMenuAnchor && handleDuplicateTask(taskMenuAnchor.task)}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => taskMenuAnchor && handleDeleteTask(taskMenuAnchor.task.id)}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setSortOption('dueDate'); setSortMenuAnchor(null); }}>
          <ListItemIcon>{sortOption === 'dueDate' && <CheckCircleIcon fontSize="small" color="primary" />}</ListItemIcon>
          Due Date
        </MenuItem>
        <MenuItem onClick={() => { setSortOption('priority'); setSortMenuAnchor(null); }}>
          <ListItemIcon>{sortOption === 'priority' && <CheckCircleIcon fontSize="small" color="primary" />}</ListItemIcon>
          Priority
        </MenuItem>
        <MenuItem onClick={() => { setSortOption('created'); setSortMenuAnchor(null); }}>
          <ListItemIcon>{sortOption === 'created' && <CheckCircleIcon fontSize="small" color="primary" />}</ListItemIcon>
          Created Date
        </MenuItem>
        <MenuItem onClick={() => { setSortOption('alphabetical'); setSortMenuAnchor(null); }}>
          <ListItemIcon>{sortOption === 'alphabetical' && <CheckCircleIcon fontSize="small" color="primary" />}</ListItemIcon>
          Alphabetical
        </MenuItem>
      </Menu>

      {/* Add/Edit Task Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Task Title"
                fullWidth
                required
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="What do you need to do?"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={newTask.projectId}
                  label="Project"
                  onChange={(e) => setNewTask({ ...newTask, projectId: e.target.value })}
                >
                  {projects.map(project => (
                    <MenuItem key={project.id} value={project.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{project.icon}</Typography>
                        {project.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newTask.status}
                  label="Status"
                  onChange={(e) => setNewTask({ ...newTask, status: e.target.value as Task['status'] })}
                >
                  <MenuItem value="not-started">Not Started</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTask.priority}
                  label="Priority"
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as Task['priority'] })}
                >
                  <MenuItem value="low">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlagIcon sx={{ color: priorityColors.low }} /> Low
                    </Box>
                  </MenuItem>
                  <MenuItem value="medium">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlagIcon sx={{ color: priorityColors.medium }} /> Medium
                    </Box>
                  </MenuItem>
                  <MenuItem value="high">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlagIcon sx={{ color: priorityColors.high }} /> High
                    </Box>
                  </MenuItem>
                  <MenuItem value="urgent">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FlagIcon sx={{ color: priorityColors.urgent }} /> Urgent
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                label="Due Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newTask.dueDate}
                onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                label="Due Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newTask.dueTime}
                onChange={(e) => setNewTask({ ...newTask, dueTime: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Estimated Time (minutes)"
                type="number"
                fullWidth
                value={newTask.estimatedTime}
                onChange={(e) => setNewTask({ ...newTask, estimatedTime: parseInt(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <ScheduleIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                multiple
                freeSolo
                options={allTags}
                value={newTask.tags || []}
                onChange={(_, newValue) => setNewTask({ ...newTask, tags: newValue })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} size="small" key={index} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Add tags" />
                )}
              />
            </Grid>

            {/* Recurrence */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newTask.isRecurring}
                    onChange={(e) => setNewTask({
                      ...newTask,
                      isRecurring: e.target.checked,
                      recurrence: e.target.checked ? { frequency: 'weekly', interval: 1 } : undefined,
                    })}
                  />
                }
                label="Repeat"
              />
              {newTask.isRecurring && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={newTask.recurrence?.frequency || 'weekly'}
                      onChange={(e) => setNewTask({
                        ...newTask,
                        recurrence: { ...newTask.recurrence!, frequency: e.target.value as 'daily' | 'weekly' | 'monthly' },
                      })}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="number"
                    label="Every"
                    value={newTask.recurrence?.interval || 1}
                    onChange={(e) => setNewTask({
                      ...newTask,
                      recurrence: { ...newTask.recurrence!, interval: parseInt(e.target.value) || 1 },
                    })}
                    sx={{ width: 80 }}
                  />
                </Box>
              )}
            </Grid>

            {/* Subtasks */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Subtasks</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddSubtask}>
                  Add Subtask
                </Button>
              </Box>
              {newTask.subtasks?.map((subtask, idx) => (
                <Box key={subtask.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Checkbox
                    size="small"
                    checked={subtask.completed}
                    onChange={(e) => {
                      const subtasks = [...(newTask.subtasks || [])];
                      subtasks[idx] = { ...subtasks[idx], completed: e.target.checked };
                      setNewTask({ ...newTask, subtasks });
                    }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    value={subtask.title}
                    placeholder="Subtask title"
                    onChange={(e) => {
                      const subtasks = [...(newTask.subtasks || [])];
                      subtasks[idx] = { ...subtasks[idx], title: e.target.value };
                      setNewTask({ ...newTask, subtasks });
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const subtasks = newTask.subtasks?.filter((_, i) => i !== idx) || [];
                      setNewTask({ ...newTask, subtasks });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>

            {/* Reminders */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Reminders</Typography>
                <Button size="small" startIcon={<ReminderIcon />} onClick={handleAddReminder}>
                  Add Reminder
                </Button>
              </Box>
              {newTask.reminders?.map((reminder, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControl size="small" sx={{ width: 130 }}>
                    <Select
                      value={reminder.type}
                      onChange={(e) => {
                        const reminders = [...(newTask.reminders || [])];
                        reminders[idx] = { ...reminders[idx], type: e.target.value as 'notification' | 'email' };
                        setNewTask({ ...newTask, reminders });
                      }}
                    >
                      <MenuItem value="notification">Notification</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="date"
                    value={reminder.date}
                    onChange={(e) => {
                      const reminders = [...(newTask.reminders || [])];
                      reminders[idx] = { ...reminders[idx], date: e.target.value };
                      setNewTask({ ...newTask, reminders });
                    }}
                  />
                  <TextField
                    size="small"
                    type="time"
                    value={reminder.time}
                    onChange={(e) => {
                      const reminders = [...(newTask.reminders || [])];
                      reminders[idx] = { ...reminders[idx], time: e.target.value };
                      setNewTask({ ...newTask, reminders });
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      const reminders = newTask.reminders?.filter((_, i) => i !== idx) || [];
                      setNewTask({ ...newTask, reminders });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>

            {/* Assignees */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={newTask.assignees || []}
                onChange={(_, newValue) => setNewTask({ ...newTask, assignees: newValue })}
                renderTags={(value, getTagProps) =>
                  value.map((email, index) => (
                    <Chip
                      avatar={<Avatar sx={{ width: 24, height: 24 }}>{email.charAt(0).toUpperCase()}</Avatar>}
                      label={email}
                      {...getTagProps({ index })}
                      size="small"
                      key={index}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Assignees" placeholder="Add assignees by email" />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTask} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Color</InputLabel>
            <Select
              value={newProjectColor}
              label="Color"
              onChange={(e) => setNewProjectColor(e.target.value)}
            >
              {['#1976d2', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#009688', '#e91e63'].map(color => (
                <MenuItem key={color} value={color}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: color, borderRadius: 1 }} />
                    {color}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateProject} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TasksPage;
